import { inject, Injectable } from '@angular/core';
import { EffectsBase } from 'clicklease-ui-elements';
import { DisclosureLoginService } from '../../services';
import * as disclosureLoginActions from './disclosure-login.actions';
import { catchError, first, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { getPhoneNumberSelector, getRoseKeySelector } from "../../ngrx";
import { ofType } from '@ngrx/effects';
import { FileSaverService } from 'ngx-filesaver';
import { log } from 'console';



@Injectable()
export class DisclosureLoginEffect extends EffectsBase {
    readonly #disclosureService = inject(DisclosureLoginService);
    readonly #fileSaverService = inject(FileSaverService);

    public readonly disclosureLoginContent = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(disclosureLoginActions.loadDisclosureLogin),
            mergeMap((data: any) => this.#disclosureService.postDisclosureLogin(data.disclosureName).pipe(
                first(),
                map((disclosureContent) => disclosureLoginActions.loadDisclosureLoginSuccess(disclosureContent)),
                catchError((error) => of(disclosureLoginActions.loadDisclosureLoginFailure(error.message)))
            )
            )
        )
    );

    private readonly roseKey = this.store.selectSignal(getRoseKeySelector);
    public readonly postDisclosureLoginSuccess = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(disclosureLoginActions.postDisclosureLogin),
            mergeMap((action) => {
                const { disclosureName, phoneNumber } = action as { disclosureName: string; phoneNumber: string }
                const requestBody = {
                    roseKey: this.roseKey(),
                    disclosureName: disclosureName,
                    attributes: [
                        {
                            name: 'MOBILE_NUMBER',
                            value: phoneNumber
                        }
                    ]
                }
                return this.#disclosureService.postDisclosureLoginSucces(requestBody).pipe(
                    first(),
                    map(() => disclosureLoginActions.postDisclosureLoginSuccess()),
                    catchError((error) => of(disclosureLoginActions.postDisclosureLoginFailure({ error: error.message })))
                );
            })
        )
    );


    //for Bank info disclusore
    public readonly loadBankDisclosure = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(disclosureLoginActions.loadBankDisclosure),
            mergeMap((action ) => {
                const typedAction = action as { disclosureName: string; returnType: string; attributes: { name: string; value: string }[] };
                return this.#disclosureService.getBankDisclosure(typedAction.disclosureName, typedAction.returnType, typedAction.attributes)
                    .pipe(
                        first(),
                        map((response) =>
                            disclosureLoginActions.loadBankDisclosureSuccess({
                                disclosureContent: response.disclosureContent,
                                contentType: response.contentType,
                                disclosureVersion: response.disclosureVersion
                            })),
                        catchError((error) => of(disclosureLoginActions.loadBankDisclosureFailure({ error: error.message })))
                    );
            })
        )
    );

    public readonly postBankDisclosure = this.createEffect(()=>
        this.actions.pipe(
            this.ofType(disclosureLoginActions.postBankDisclosure),
            mergeMap((action) =>{
                const typedAction = action as { roseKey: string; disclosureName: string; attributes: { name: string; value: string }[] };
                return this.#disclosureService.postBankDisclosure(typedAction.roseKey, typedAction.disclosureName, typedAction.attributes).pipe(
                    first(),
                    map(() => disclosureLoginActions.postBankDisclosureSuccess()),
                    catchError((error) => of(disclosureLoginActions.postBankDisclosureFailure({error: error.message})))
                )
            })
        )
    );

    public readonly downloadBankDisclosure = this.createEffect(() =>
        this.actions.pipe(
            ofType(disclosureLoginActions.downloadBankDisclosureContent),
            switchMap(({ disclosureName, returnType, attributes }) =>
                this.#disclosureService.getBankDisclosure(disclosureName, returnType, attributes).pipe(
                    map((response) => {
                        if (!response.disclosureContent) {
                            throw new Error('No pdf file attached');
                        }
                        const byteCharacters = atob(response.disclosureContent);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: 'application/pdf' });
                        //download
                        this.#fileSaverService.save(blob, 'Bank_Disclosure.pdf');
                        return {type: '[Disclosure] Download Success'}
                    }),
                    catchError((error) => of(disclosureLoginActions.loadBankDisclosureFailure({ error: error.message })))
                )
            )
        ),
    );

};
